import React, { useState, UIEvent, useEffect, useRef } from "react";
import { AlertTriangle, Check } from "lucide-react";

interface Props {
  isOpen: boolean;
  onAccept?: (dontShowFor30Days?: boolean) => void;
  onClose?: () => void;
  viewOnly?: boolean;
}

const DisclaimerModal = ({ isOpen, onAccept, onClose, viewOnly }: Props) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [dontShowFor30Days, setDontShowFor30Days] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    let animationFrameId: number;

    const checkScrollable = () => {
      const el = scrollRef.current;
      if (!el) return;

      // If the content is smaller than the container, or there's very little left to scroll (< 60px),
      // we auto-enable the checkbox so the user doesn't have to awkwardly scroll just a few pixels.
      if (Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight - 60) {
        setHasScrolledToBottom(true);
      }
    };

    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(checkScrollable);
    });

    if (scrollRef.current) {
      observer.observe(scrollRef.current);
      // Observe the inner content as well, so if text loads/reflows, we re-check
      if (scrollRef.current.firstElementChild) {
        observer.observe(scrollRef.current.firstElementChild);
      }
    }

    // Initial check
    animationFrameId = requestAnimationFrame(checkScrollable);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(animationFrameId);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (Math.ceil(scrollTop + clientHeight) >= scrollHeight - 60) {
      setHasScrolledToBottom(true);
    }
  };

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-black/30 dark:bg-black/80 backdrop-blur-xl animate-in fade-in duration-300">
      <div
        className="bg-white dark:bg-[#111827] border border-gray-200 dark:border-white/10 rounded-[20px] shadow-2xl shadow-black/10 dark:shadow-black/50 w-full max-w-4xl flex flex-col max-h-[90vh] overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-2.5 flex items-center justify-between shrink-0 relative z-10 border-b border-gray-100 dark:border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-transparent border border-red-200 dark:border-red-500/30 rounded-lg flex items-center justify-center shrink-0">
              <AlertTriangle
                className="w-3.5 h-3.5 text-red-500 dark:text-red-400"
                strokeWidth={1.5}
              />
            </div>
            <h2 className="text-[15px] font-bold text-gray-900 dark:text-white tracking-tight leading-tight">
              Disclaimer
            </h2>
          </div>
          {viewOnly && onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
        </div>

        {/* Scrollable Content */}
        <div
          ref={scrollRef}
          className="px-5 py-3 overflow-y-auto custom-scrollbar flex-1 relative z-10"
          onScroll={handleScroll}
        >
          <div className="space-y-4">
            <div className="flex gap-3.5">
              <div>
                <h3 className="text-[14.5px] font-bold text-gray-900 dark:text-gray-100 mb-1.5">
                  1. High-Risk Investment Warning
                </h3>
                <p className="text-[13.5px] text-gray-600 dark:text-gray-400 leading-relaxed">
                  Trading and investing in futures, equities (stocks), and
                  foreign exchange (forex/spot currency) involves a substantial
                  risk of loss and is not suitable for all investors. The
                  potential for significant rewards is accompanied by the
                  potential for significant financial risk. By utilizing this
                  platform, you acknowledge and agree that you are fully aware
                  of these risks and are expressly willing to accept them in
                  order to participate in the aforementioned financial markets.
                </p>
              </div>
            </div>

            <div className="flex gap-3.5">
              <div>
                <h3 className="text-[14.5px] font-bold text-gray-900 dark:text-gray-100 mb-1.5">
                  2. Risk Capital Only
                </h3>
                <p className="text-[13.5px] text-gray-600 dark:text-gray-400 leading-relaxed">
                  You should never trade or invest with funds you cannot afford
                  to lose. All trading should be conducted strictly utilizing
                  risk capital—disposable funds that, if entirely lost, would
                  not adversely impact your lifestyle or financial security.
                </p>
              </div>
            </div>

            <div className="flex gap-3.5">
              <div>
                <h3 className="text-[14.5px] font-bold text-gray-900 dark:text-gray-100 mb-1.5">
                  3. No Offer or Solicitation; Educational Purposes Only
                </h3>
                <p className="text-[13.5px] text-gray-600 dark:text-gray-400 leading-relaxed">
                  All information, data, materials, and services provided on the
                  ABillion Analytics platform (&quot;Platform&quot;) are
                  intended strictly for informational and educational purposes.
                  Nothing contained herein shall be construed as a solicitation,
                  recommendation, or offer to buy, sell, or hold any futures
                  contracts, equities, foreign exchange, or other financial
                  instruments.
                </p>
              </div>
            </div>

            <div className="flex gap-3.5">
              <div>
                <h3 className="text-[14.5px] font-bold text-gray-900 dark:text-gray-100 mb-1.5">
                  4. Past Performance and Market Volatility
                </h3>
                <p className="text-[13.5px] text-gray-600 dark:text-gray-400 leading-relaxed">
                  Past performance of any trading methodology, indicator, or
                  strategy is not necessarily indicative of future results.
                  ABillion Analytics makes no representation, warranty, or
                  guarantee that any user or account will, or is likely to,
                  achieve profits or incur losses similar to those historically
                  observed. Global financial markets are highly volatile and
                  subject to unforeseen macroeconomic, geopolitical, and
                  systemic events that can materially alter market conditions
                  and investment outcomes.
                </p>
              </div>
            </div>

            <div className="flex gap-3.5">
              <div>
                <h3 className="text-[14.5px] font-bold text-gray-900 dark:text-gray-100 mb-1.5">
                  5. Suitability and Professional Consultation
                </h3>
                <p className="text-[13.5px] text-gray-600 dark:text-gray-400 leading-relaxed">
                  The generalized strategies, advice, and methodologies provided
                  on this Platform may not be suitable or appropriate for your
                  specific financial situation, investment objectives, or risk
                  tolerance. You are strictly advised to consult with a
                  qualified, independent financial advisor, legal counsel,
                  and/or tax professional prior to executing any investments or
                  trades.
                </p>
              </div>
            </div>

            <div className="flex gap-3.5">
              <div>
                <h3 className="text-[14.5px] font-bold text-gray-900 dark:text-gray-100 mb-1.5">
                  6. Limitation of Liability
                </h3>
                <p className="text-[12px] text-gray-600 dark:text-gray-400 uppercase tracking-wide leading-relaxed font-semibold">
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, ABILLION
                  ANALYTICS, ITS AFFILIATES, SUBSIDIARIES, OFFICERS, DIRECTORS,
                  EMPLOYEES, AGENTS, AND ASSOCIATED ENTITIES SHALL IN NO EVENT
                  BE HELD LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
                  CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES. THIS INCLUDES,
                  BUT IS NOT LIMITED TO, DAMAGES FOR LOSS OF PROFITS, REVENUE,
                  GOODWILL, DATA, OR OTHER INTANGIBLE OR COMMERCIAL LOSSES
                  ARISING OUT OF, OR IN CONNECTION WITH, YOUR USE OR INABILITY
                  TO USE THE PLATFORM OR RELIANCE ON ANY INFORMATION PROVIDED
                  HEREIN, REGARDLESS OF WHETHER ABILLION ANALYTICS HAS BEEN
                  ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions / Bottom View */}
        {!viewOnly && (
          <div className="px-5 py-2.5 shrink-0 relative z-10 border-t border-gray-100 dark:border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex flex-col gap-1.5">
              <label
                className={`flex items-center gap-2.5 select-none transition-all duration-300 ${hasScrolledToBottom ? "opacity-100 cursor-pointer" : "opacity-40 cursor-not-allowed"}`}
              >
                <div
                  className={`w-4.5 h-4.5 rounded-[5px] border flex items-center justify-center transition-all duration-200 shrink-0 ${isChecked ? "bg-blue-600 border-blue-600 shadow-[0_0_10px_rgba(37,99,235,0.5)]" : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"}`}
                >
                  {isChecked && (
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  )}
                </div>
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => setIsChecked(e.target.checked)}
                  disabled={!hasScrolledToBottom}
                  className="hidden"
                />
                <span className="text-[13px] font-medium text-gray-700 dark:text-gray-200">
                  I have read and agree to the Disclaimer
                </span>
              </label>

              <label
                className={`flex items-center gap-2.5 select-none transition-all duration-300 ${hasScrolledToBottom ? "opacity-100 cursor-pointer" : "opacity-40 cursor-not-allowed"}`}
              >
                <div
                  className={`w-4.5 h-4.5 rounded-[5px] border flex items-center justify-center transition-all duration-200 shrink-0 ${dontShowFor30Days ? "bg-blue-600 border-blue-600 shadow-[0_0_10px_rgba(37,99,235,0.5)]" : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"}`}
                >
                  {dontShowFor30Days && (
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  )}
                </div>
                <input
                  type="checkbox"
                  checked={dontShowFor30Days}
                  onChange={(e) => setDontShowFor30Days(e.target.checked)}
                  disabled={!hasScrolledToBottom}
                  className="hidden"
                />
                <span className="text-[13px] font-medium text-gray-700 dark:text-gray-200">
                  Do not show again for the next 30 days
                </span>
              </label>
            </div>

            <button
              onClick={() => onAccept?.(dontShowFor30Days)}
              disabled={!isChecked}
              className={`py-2 px-5 rounded-lg font-bold text-[13.5px] transition-all duration-300 w-full sm:w-auto shrink-0 ${
                isChecked
                  ? "bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_15px_rgba(37,99,235,0.3)] hover:shadow-[0_0_20px_rgba(37,99,235,0.4)] active:scale-95 cursor-pointer"
                  : "bg-gray-200 dark:bg-white/5 text-gray-400 dark:text-gray-500 cursor-not-allowed"
              }`}
            >
              Agree & Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DisclaimerModal;
